import { requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

const productGridDesktopQuery = matchMedia('(min-width: 1280px)');

const PRODUCT_GRID_VIEW_ALLOWED = new Set(['default', 'single', 'mobile-single']);
const COLLECTION_PRODUCT_SLIDER_SINGLE_PER_PAGE = 1;

/**
 * @param {string} value
 * @returns {string}
 */
function normalizeProductGridView(value) {
  return PRODUCT_GRID_VIEW_ALLOWED.has(value) ? value : 'default';
}

/**
 * @param {string} layout
 * @returns {'desktop-single' | 'mobile-single' | null}
 */
function getCollectionSliderSingleColumnMode(layout) {
  const normalized = normalizeProductGridView(layout);

  if (normalized === 'single') return 'desktop-single';
  if (normalized === 'mobile-single') return 'mobile-single';

  return null;
}

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    productGridDesktopQuery.addEventListener('change', this.#handleMediaQueryChange);
    this.#syncCollectionProductSliders(this.#readStoredLayout());
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    productGridDesktopQuery.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    const layout = normalizeProductGridView(value);

    await startViewTransition(() => this.#setLayout(layout), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = productGridDesktopQuery.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, layout);
    });
  };

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;

    grid.setAttribute('product-grid-view', value);
    this.#syncCollectionProductSliders(value);
  }

  /**
   * Reads the saved layout for the current viewport.
   *
   * @returns {string}
   */
  #readStoredLayout() {
    const viewport = productGridDesktopQuery.matches ? 'desktop' : 'mobile';
    const fallback = viewport === 'desktop' ? 'single' : 'mobile-single';
    return normalizeProductGridView(sessionStorage.getItem(`product-grid-view-${viewport}`) || fallback);
  }

  /**
   * Stores theme-editor Splide options before grid-layout overrides are applied.
   *
   * @param {HTMLElement} slider
   * @param {HTMLElement | null} wrapper
   */
  #cacheCollectionSliderBaseline(slider, wrapper) {
    if (!slider.dataset.collectionSliderBaseSplide) {
      slider.dataset.collectionSliderBaseSplide = slider.dataset.splide || '{}';
    }

    if (wrapper instanceof HTMLElement && !wrapper.dataset.collectionSliderBaseStyles) {
      wrapper.dataset.collectionSliderBaseStyles = JSON.stringify({
        perPage: wrapper.style.getPropertyValue('--splide-per-page').trim(),
        perPageSmallDesktop: wrapper.style.getPropertyValue('--splide-per-page-small-desktop').trim(),
        perPageTablet: wrapper.style.getPropertyValue('--splide-per-page-tablet').trim(),
        perPageMobile: wrapper.style.getPropertyValue('--splide-per-page-mobile').trim(),
      });
    }
  }

  /**
   * Restores theme-editor slider CSS variables after a layout override is cleared.
   *
   * @param {HTMLElement} wrapper
   * @param {Record<string, string>} baseStyles
   */
  #restoreCollectionSliderWrapperStyles(wrapper, baseStyles) {
    const styleKeys = [
      ['--splide-per-page', 'perPage'],
      ['--splide-per-page-small-desktop', 'perPageSmallDesktop'],
      ['--splide-per-page-tablet', 'perPageTablet'],
      ['--splide-per-page-mobile', 'perPageMobile'],
    ];

    for (const [cssVar, baseKey] of styleKeys) {
      const value = baseStyles[baseKey];

      if (value) {
        wrapper.style.setProperty(cssVar, value);
      } else {
        wrapper.style.removeProperty(cssVar);
      }
    }
  }

  /**
   * Keeps the below-grid collection product slider aligned with single-column grid views
   * while preserving theme-editor per-page settings for the default layout.
   *
   * @param {string} value
   */
  #syncCollectionProductSliders(value) {
    const singleColumnMode = getCollectionSliderSingleColumnMode(value);

    for (const slider of this.querySelectorAll('[data-collection-product-slider] splide-slider')) {
      if (!(slider instanceof HTMLElement)) continue;

      const wrapper = slider.closest('.splide-slider-wrapper');
      this.#cacheCollectionSliderBaseline(slider, wrapper instanceof HTMLElement ? wrapper : null);

      const baseStyles = JSON.parse(
        wrapper instanceof HTMLElement ? wrapper.dataset.collectionSliderBaseStyles || '{}' : '{}'
      );

      const options = JSON.parse(slider.dataset.collectionSliderBaseSplide || '{}');

      if (singleColumnMode === 'desktop-single') {
        options.perPage = COLLECTION_PRODUCT_SLIDER_SINGLE_PER_PAGE;
        options.breakpoints = options.breakpoints || {};
        options.breakpoints['1439'] = {
          ...(options.breakpoints['1439'] || {}),
          perPage: COLLECTION_PRODUCT_SLIDER_SINGLE_PER_PAGE,
        };
      } else if (singleColumnMode === 'mobile-single') {
        options.breakpoints = options.breakpoints || {};
        options.breakpoints['1439'] = {
          ...(options.breakpoints['1439'] || {}),
          perPage: COLLECTION_PRODUCT_SLIDER_SINGLE_PER_PAGE,
        };

        if (options.breakpoints['989']) {
          options.breakpoints['989'] = {
            ...options.breakpoints['989'],
            perPage: COLLECTION_PRODUCT_SLIDER_SINGLE_PER_PAGE,
          };
        }

        options.breakpoints['749'] = {
          ...(options.breakpoints['749'] || {}),
          perPage: COLLECTION_PRODUCT_SLIDER_SINGLE_PER_PAGE,
        };
      }

      slider.dataset.splide = JSON.stringify(options);

      if (wrapper instanceof HTMLElement) {
        if (singleColumnMode === 'desktop-single') {
          wrapper.style.setProperty('--splide-per-page', String(COLLECTION_PRODUCT_SLIDER_SINGLE_PER_PAGE));
          wrapper.style.setProperty(
            '--splide-per-page-small-desktop',
            String(COLLECTION_PRODUCT_SLIDER_SINGLE_PER_PAGE)
          );
        } else if (singleColumnMode === 'mobile-single') {
          wrapper.style.setProperty(
            '--splide-per-page-small-desktop',
            String(COLLECTION_PRODUCT_SLIDER_SINGLE_PER_PAGE)
          );
          wrapper.style.setProperty('--splide-per-page-tablet', String(COLLECTION_PRODUCT_SLIDER_SINGLE_PER_PAGE));
          wrapper.style.setProperty('--splide-per-page-mobile', String(COLLECTION_PRODUCT_SLIDER_SINGLE_PER_PAGE));
        } else {
          this.#restoreCollectionSliderWrapperStyles(wrapper, baseStyles);
        }
      }

      if (typeof slider.initializeSplide === 'function') {
        slider.initializeSplide(slider);
      }
    }
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const layout = event.matches ? 'single' : 'mobile-single';
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-single-option"]')
      : this.querySelector('[data-grid-layout="mobile-single-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout(layout);
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
